// ─── ProjectMapView ─────────────────────────────────────────────────────────
// "Project Map" dashboard tab. Renders a full-bleed Leaflet map with one
// coloured pin per site that has GPS coordinates. The Edit button in the
// top-right corner opens MapEditorModal where the user can tweak pin colours,
// pick the base tile layer, and set the default centre + zoom.
//
// Data:
//   - sites prop comes from DashboardPage's projectSites state (the same
//     /api/project/{id}/sites/ payload used by the sidebar).
//   - Map configuration (pin colours, tile layer, centre + zoom) is read
//     from and persisted to localStorage by mapConfig helpers, keyed per
//     project so each project keeps its own map setup.
//
// Layer-switching:
//   - The TileLayer's `key` prop is bound to the active tile layer key so
//     React fully remounts the layer on switch — the simplest way to swap
//     URL/attribution/maxZoom together without leaking state.
//
// Phase 2 (not in this commit): BYO Google/Esri API key from Account
// settings — at render time we'd swap in a Google/Esri sub-renderer based
// on the user's chosen provider. Today: OSM, OpenTopoMap, and the free
// Esri World Imagery layer (no key required).
// ────────────────────────────────────────────────────────────────────────────

import { useMemo, useState, useEffect } from 'react'
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet'
import { Pencil, MapPin } from 'lucide-react'
import MapEditorModal from './MapEditorModal'
import { useDashboardContext } from '../../../context/DashboardContext'
import { projectsApi } from '../../../lib/projectsApi'
import {
    getTileLayer,
    inferSiteType,
    loadMapConfig,
    saveMapConfig,
} from './mapConfig'

// Imperative helper: when the saved config has no explicit centre, fit the
// map to the bounding box of all site markers. Falls back to a global view
// if the project has no geocoded sites at all.
function FitBoundsOnce({ sites, configCenter }) {
    const map = useMap()
    useEffect(() => {
        // Honour an explicit centre if the user has saved one.
        if (configCenter && Number.isFinite(configCenter.lat) && Number.isFinite(configCenter.lng)) {
            map.setView([configCenter.lat, configCenter.lng], configCenter.zoom || 8)
            return
        }
        const points = sites
            .filter((s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude))
            .map((s) => [s.latitude, s.longitude])
        if (points.length === 0) {
            map.setView([20, 0], 2)
        } else if (points.length === 1) {
            map.setView(points[0], 10)
        } else {
            map.fitBounds(points, { padding: [40, 40] })
        }
    // Re-fit when the set of sites changes — but NOT when configCenter
    // changes mid-render, since we always defer to the saved centre when
    // present.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sites.length])
    return null
}

// ── Site marker popup ───────────────────────────────────────────────────────
// Matches the legacy Symfony popup layout: site name in the header, a
// Data / Gallery tab strip, and (under Data) a list of every device on the
// site with the device's last-contact timestamp underneath — same shape
// the customer saw on the old map.
//
// The Gallery tab currently shows a placeholder; per-site photo filtering
// will be wired in a follow-up once the gallery API supports a `site=` filter.
// Until then, hiding the tab would diverge from the legacy parity the user
// asked for, so it's present but flagged.
function fmtTimestamp(iso) {
    if (!iso) return ''
    try {
        const d = new Date(iso)
        if (Number.isNaN(d.getTime())) return ''
        const pad = (n) => String(n).padStart(2, '0')
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    } catch {
        return ''
    }
}

function SiteMarkerPopup({ site, selectedParameters = [] }) {
    const devices = Array.isArray(site.devices) ? site.devices : []
    const selectedIds = new Set(selectedParameters.map((p) => p.id))

    // Per logger, the parameters to show = flagged "Map" in Parameter Config OR
    // currently checked in the sidebar — matching the XD, where the popup lists
    // the selected parameters' latest readings as "<param> (<unit>) : <value>".
    const perDevice = devices.map((d) => ({
        id: d.id,
        name: d.name || d.uid || `Device ${d.id}`,
        last: d.last_contact_time,
        params: (Array.isArray(d.sensors) ? d.sensors : [])
            .flatMap((s) => (Array.isArray(s.parameters) ? s.parameters : []))
            .filter((p) => p.show_map_tooltip || selectedIds.has(p.id)),
    }))

    // Sites API only carries latest values for Map-flagged params; fetch the
    // rest (selected-but-not-flagged) on demand.
    const needIds = perDevice.flatMap((d) => d.params).filter((p) => p.latest_value == null).map((p) => p.id)
    const needKey = needIds.join(',')
    const [fetched, setFetched] = useState({})
    useEffect(() => {
        if (!needKey) return
        let cancelled = false
        Promise.allSettled(needKey.split(',').map((id) =>
            projectsApi.fetchGraphData(Number(id), {}).then((res) => ({ id, res }))
        )).then((results) => {
            if (cancelled) return
            const map = {}
            results.forEach((r) => {
                if (r.status === 'fulfilled' && r.value?.res && !r.value.res.error) {
                    map[r.value.id] = { value: r.value.res.latestValue, unit: r.value.res.unit }
                }
            })
            setFetched(map)
        })
        return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needKey])

    const valueFor = (p) => {
        if (p.latest_value != null) return { value: p.latest_value, unit: p.latest_unit || p.unit || '' }
        const f = fetched[String(p.id)]
        if (f && f.value != null) return { value: f.value, unit: f.unit || p.unit || '' }
        return { value: null, unit: p.unit || '' }
    }

    return (
        <div className="text-sm min-w-[240px]">
            {/* Site name header (no inferred "Land Based Station" chip, no tabs). */}
            <div className="font-bold text-gray-900 text-base mb-2">{site.name}</div>

            {perDevice.length === 0 ? (
                <div className="text-gray-500 italic text-xs">No loggers on this site.</div>
            ) : (
                <div className="space-y-2.5 max-h-60 overflow-y-auto pr-1">
                    {perDevice.map((d) => (
                        <div key={d.id} className="leading-snug">
                            <div className="font-semibold text-gray-800 text-sm">{d.name}</div>
                            {d.last && (
                                <div className="text-xs text-gray-500">{fmtTimestamp(d.last)}</div>
                            )}
                            {d.params.length > 0 && (
                                <div className="mt-1 space-y-1">
                                    {d.params.map((p) => {
                                        const { value, unit } = valueFor(p)
                                        return (
                                            <div key={p.id} className="text-sm text-gray-700">
                                                {p.name}{unit ? ` (${unit})` : ''} :{' '}
                                                <span className="font-bold text-gray-900">
                                                    {value != null ? `${value}${unit ? ` ${unit}` : ''}` : '…'}
                                                </span>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default function ProjectMapView({ sites = [], projectId }) {
    // The Edit button only shows in dashboard Edit Mode (and for editors).
    const { isCustomizing = false, canEdit = true, selectedParameters = [] } = useDashboardContext()
    const [config, setConfig] = useState(() => loadMapConfig(projectId))
    const [editorOpen, setEditorOpen] = useState(false)

    // Reload config when the active project changes so opening Project Map
    // on a different project doesn't show another project's pin colours.
    useEffect(() => {
        setConfig(loadMapConfig(projectId))
    }, [projectId])

    // Only sites with usable coordinates can be rendered as pins. Accept either
    // the API's `latitude`/`longitude` OR the raw model fields
    // `sample_pt_latitude`/`sample_pt_longitude` — a just-created/edited site
    // added to the list straight from the SiteViewSet response carries the
    // latter, and would otherwise vanish from the map until a full reload.
    const geocodedSites = useMemo(
        () => sites
            .map((s) => ({
                ...s,
                latitude:  Number(s.latitude  ?? s.sample_pt_latitude),
                longitude: Number(s.longitude ?? s.sample_pt_longitude),
            }))
            .filter((s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude)),
        [sites]
    )

    const tile = getTileLayer(config.tileLayer)
    const explicitCenter =
        Number.isFinite(config.centerLat) && Number.isFinite(config.centerLng)
            ? { lat: config.centerLat, lng: config.centerLng, zoom: config.zoom }
            : null

    const initialCenter = explicitCenter
        ? [explicitCenter.lat, explicitCenter.lng]
        : geocodedSites.length > 0
            ? [geocodedSites[0].latitude, geocodedSites[0].longitude]
            : [20, 0]
    const initialZoom = explicitCenter?.zoom ?? (geocodedSites.length > 0 ? 8 : 2)

    const handleSave = (next) => {
        setConfig(next)
        saveMapConfig(projectId, next)
        setEditorOpen(false)
    }

    // Empty state — project has sites but none of them have coordinates.
    // Still render the map so the Edit button is reachable.
    const showNoPinsHint = sites.length > 0 && geocodedSites.length === 0

    return (
        <div className="relative h-[calc(100vh-200px)] min-h-[500px] bg-white rounded shadow-sm overflow-hidden">
            {/* Edit button — top-right floating control.
                z-index has to clear Leaflet's pane stack (tilePane 200,
                overlayPane 400, markerPane 600, popupPane 700, control
                container 1000). z-[1100] puts the button + the no-pins
                banner above every layer the map can draw on top of. */}
            {isCustomizing && canEdit && (
                <button
                    type="button"
                    onClick={() => setEditorOpen(true)}
                    title="Edit map settings"
                    className="absolute top-3 right-3 z-[1100] flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded shadow text-sm text-gray-700 hover:bg-gray-50"
                >
                    <Pencil className="w-3.5 h-3.5" />
                    <span>Edit</span>
                </button>
            )}

            {showNoPinsHint && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1100] flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 shadow-sm">
                    <MapPin className="w-3.5 h-3.5" />
                    No sites in this project have latitude / longitude set.
                </div>
            )}

            <MapContainer
                // Key on tile layer so a layer change in the editor remounts
                // the whole map — the cheapest way to ensure attribution,
                // maxZoom and URL all swap atomically.
                key={`${tile.key}-${projectId || 'none'}`}
                center={initialCenter}
                zoom={initialZoom}
                style={{ width: '100%', height: '100%' }}
                scrollWheelZoom
            >
                <TileLayer
                    url={tile.url}
                    attribution={tile.attribution}
                    maxZoom={tile.maxZoom}
                />

                <FitBoundsOnce sites={geocodedSites} configCenter={explicitCenter} />

                {geocodedSites.map((site) => {
                    const bucket = inferSiteType(site)
                    const color = config.pinColors[bucket] || '#358ECD'
                    return (
                        <CircleMarker
                            key={site.id}
                            center={[site.latitude, site.longitude]}
                            radius={9}
                            pathOptions={{
                                color: '#ffffff',
                                weight: 2,
                                fillColor: color,
                                fillOpacity: 0.95,
                            }}
                        >
                            <Popup minWidth={220} maxWidth={280}>
                                <SiteMarkerPopup site={site} selectedParameters={selectedParameters} />
                            </Popup>
                        </CircleMarker>
                    )
                })}
            </MapContainer>

            <MapEditorModal
                open={editorOpen}
                initialConfig={config}
                onSave={handleSave}
                onCancel={() => setEditorOpen(false)}
            />
        </div>
    )
}
